<script setup lang="ts">
import gsap from 'gsap'
import { useProgress } from '@tresjs/cientos'

defineProps<{
  enabled: boolean
}>()

const { isMobileOrTablet } = useDevice()

const isScriptLoaded = ref(false)
const canvasRef = ref<HTMLDivElement | null>(null)

const options = {
  shadows: false, // false for performance
  alpha: true,
  powerPreference: 'high-performance',
  antialias: false,
  stencil: true,
  depth: false,
  windowSize: false,
  multisampling: 8
}

const appConfig = useAppConfig()
const isUnveiled = ref(false)
const { hasFinishLoading, progress } = await useProgress()

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null
const overlayRef = ref<HTMLElement | null>(null)
const { bodyLock, bodyUnlock } = useBodyLock()

watch(
  hasFinishLoading,
  () => {
    isUnveiled.value = true
    bodyUnlock()
  },
  { once: true }
)

onMounted(() => {
  if (!isMobileOrTablet) {
    const script = document.createElement('script')
    script.src = '/prebuilt/main-white.min.js'
    script.type = 'text/javascript'
    script.defer = true
    script.onload = () => {}

    if (canvasRef.value) {
      canvasRef.value.appendChild(script)
      isScriptLoaded.value = true
    }
  }

  bodyLock()
  if (!appConfig.ui.enable3D) {
    isUnveiled.value = true
    bodyUnlock()
  }
  if (ctxRef.value) {
    ctx = gsap.context((self: gsap.Context) => {
      const elOverlay = self.selector ? self.selector(overlayRef.value) : null

      gsap.set(elOverlay, { autoAlpha: 0 })

      gsap.to(elOverlay, {
        autoAlpha: 1,
        duration: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: document.querySelector('#home-content-scroll') as HTMLElement,
          scrub: true,
          start: `+=${window.innerHeight / 5} bottom`,
          end: 'top 69%',
          markers: false
        }
      })

      gsap.to(ctxRef.value, {
        y: -(window.innerHeight * 1.5),
        ease: 'none',
        scrollTrigger: {
          trigger: document.querySelector('#home-content-scroll') as HTMLElement,
          scrub: true,
          start: `top top`,
          end: 'bottom top',
          markers: false
        }
      })
    }, ctxRef.value as HTMLElement)
  }
})

onBeforeUnmount(() => {
  if (!isMobileOrTablet) {
    if (canvasRef.value) {
      let canvasElement = canvasRef.value.querySelector('canvas')
      if (canvasElement) {
        canvasElement = null
      }
    }
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      name="fade-overlay"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
      enter-active-class="transition-opacity duration-500"
      leave-active-class="transition-opacity duration-500"
    >
      <AppLoading v-if="!isUnveiled" :progress="progress" />
    </Transition>
  </Teleport>
  <div ref="ctxRef" class="fixed left-0 top-0 h-screen w-screen bg-white">
    <div id="blob-canvas" ref="canvasRef" class="fixed" />
    <TresCanvas v-if="true" v-bind="options" :window-size="false">
      <CanvasRenderer />

      <Suspense>
        <CanvasSpaceship ref="spaceShipRef" />
      </Suspense>

      <!--<Stats />-->
    </TresCanvas>
    <CanvasFallback v-else />

    <div ref="overlayRef" class="absolute left-0 top-0 h-full w-full bg-white" />
  </div>
</template>
